import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../Common/Configurations/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import Header from '../../Common/Layout/Header';
import SubscriptionTabs from '../Subscriptions/SubscriptionsTabs';
import DeleteAlert from '../../Common/Layout/DeleteAlert';
import { format } from 'date-fns';
import Modal from 'react-bootstrap/Modal';

const planTypes = [
    "MICRO",
    "TRIAL",
    "BASIC",
    "STARTER"
];

const parseUTC = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    return dateStr.endsWith("Z") ? new Date(dateStr) : new Date(dateStr + "Z");
};
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const parsedDate = parseUTC(dateString);
    if (!parsedDate || isNaN(parsedDate.getTime())) return '-';
    return format(parsedDate, 'dd MMM, yyyy hh:mm a');
};

const ClientSubscriptions = () => {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);

    // Search and filter states
    const [searchTerm, setSearchTerm] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [selectedPlanType, setSelectedPlanType] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // clients list for suggestions
    const [allClients, setAllClients] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);

    // View Details Modal
    const [viewShow, setViewShow] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);

    const filtersRef = useRef({
        searchTerm: "",
        companyId: null,
        planTypeFilter: "",
        statusFilter: "",
        fromDate: "",
        toDate: "",
        pageNumber: 1
    });

    const [availablePlans, setAvailablePlans] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [sendingEmailId, setSendingEmailId] = useState(null);
    const [errors, setErrors] = useState({});
    const [formError, setFormError] = useState("");

    // Counts state
    const [counts, setCounts] = useState({
        totalSubscriptions: 0,
        activeCount: 0,
        pendingCount: 0,
        suspendedCount: 0,
        cancelledCount: 0,
        expiredCount: 0,
        cancelRequestedCount: 0,
        microCount: 0,
        trialCount: 0,
        basicCount: 0,
        starterCount: 0
    });

    // Separate Modal States to avoid overlap/bugs
    const [showEditModal, setShowEditModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [activeTab, setActiveTab] = useState("subscription");

    // ==========================================
    // FORM STATE 1: Administrative In-Place Edit
    // ==========================================
    const [subId, setSubId] = useState("");
    const [subPlanId, setSubPlanId] = useState("");
    const [rzpSubId, setRzpSubId] = useState("");
    const [rzpCustId, setRzpCustId] = useState("");
    const [subStartDate, setSubStartDate] = useState("");
    const [subEndDate, setSubEndDate] = useState("");
    const [subStatus, setSubStatus] = useState("Active");
    const [subCreatedDate, setSubCreatedDate] = useState("");
    const [subPaymentId, setSubPaymentId] = useState("");
    const [rzpOrderId, setRzpOrderId] = useState("");
    const [rzpPaymentId, setRzpPaymentId] = useState("");
    const [paymentAmount, setPaymentAmount] = useState("");
    const [setupFee, setSetupFee] = useState("0");
    const [subFee, setSubFee] = useState("0");
    const [payStatus, setPayStatus] = useState("Success");
    const [payCreatedDate, setPayCreatedDate] = useState("");
    const [invoiceNo, setInvoiceNo] = useState("");

    // ==========================================
    // FORM STATE 2: Clean Manual Upgrade (7 Params)
    // ==========================================
    const [upgradePlanId, setUpgradePlanId] = useState("");
    const [upgradeRzpCustId, setUpgradeRzpCustId] = useState("");
    const [upgradeRzpSubId, setUpgradeRzpSubId] = useState("");
    const [upgradeRzpPaymentId, setUpgradeRzpPaymentId] = useState("");
    const [upgradePaymentDate, setUpgradePaymentDate] = useState("");
    const [upgradeAmount, setUpgradeAmount] = useState("");

    // Transaction Logs States
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [transactionLogs, setTransactionLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);

    // Payments History States
    const [showPaymentsModal, setShowPaymentsModal] = useState(false);
    const [paymentsList, setPaymentsList] = useState([]);
    const [loadingPayments, setLoadingPayments] = useState(false);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    // Fetch Subscriptions data
    const getSubscriptions = useCallback(async (currentPage = 1) => {
        if (!user) return;
        setLoading(true);
        try {
            const currentSearch = filtersRef.current.searchTerm;
            const currentCompanyId = filtersRef.current.companyId;
            const currentPlanType = filtersRef.current.planTypeFilter;
            const currentStatus = filtersRef.current.statusFilter;
            const currentFromDate = filtersRef.current.fromDate;
            const currentToDate = filtersRef.current.toDate;
            const currentPageNumber = currentPage || filtersRef.current.pageNumber;

            const params = new URLSearchParams();
            params.append('PageNumber', currentPageNumber);
            params.append('PageSize', pageSize);

            if (currentCompanyId) {
                params.append('companyId', currentCompanyId);
            }
            if (currentSearch && currentSearch.trim()) {
                params.append('search', currentSearch.trim());
            }
            if (currentPlanType) {
                params.append('planType', currentPlanType);
            }
            if (currentStatus !== "") {
                params.append('status', currentStatus);
            }
            if (currentFromDate) {
                params.append('fromDate', currentFromDate);
            }
            if (currentToDate) {
                params.append('toDate', currentToDate);
            }

            const url = `${config.GETALLSUBSCRIPTIONS}?${params.toString()}`;
            const response = await apiService.post(url);

            setSubscriptions(response.data || []);
            setTotalPages(response.totalPages || 0);
            setTotalRecords(response.totalRecords || 0);
            setPageNumber(response.pageNumber || 1);

            filtersRef.current.pageNumber = currentPageNumber;
        } catch (error) {
            console.error("Failed to fetch subscriptions:", error);
            setSubscriptions([]);
        } finally {
            setLoading(false);
        }
    }, [user, pageSize]);

    const fetchAllClientsForSuggestions = useCallback(async () => {
        try {
            const resourceUrl = `${config.GETALLCOMPANIES}?companyType=CLIENT`;
            const response = await apiService.getAll(resourceUrl);
            const data = Array.isArray(response) ? response : (response?.data || []);
            setAllClients(data);
        } catch (error) {
            console.error("Failed to preload unpaginated clients for suggestions:", error);
        }
    }, []);

    const fetchActivePlans = useCallback(async () => {
        try {
            const response = await apiService.getSingle(config.GETALLSUBSCRIPTIONPLANS);
            const activePlans = (response || []).filter(p => p.isActive);
            setAvailablePlans(activePlans);
        } catch (error) {
            console.error("Failed to fetch active plans for manual billing:", error);
        }
    }, []);

    const fetchSubscriptionCounts = useCallback(async () => {
        if (!user) return;
        try {
            const params = new URLSearchParams();
            
            if (filtersRef.current.companyId) {
                params.append('companyId', filtersRef.current.companyId);
            }
            if (filtersRef.current.planTypeFilter) {
                params.append('planType', filtersRef.current.planTypeFilter);
            }
            if (filtersRef.current.statusFilter !== "") {
                params.append('status', filtersRef.current.statusFilter);
            }
            if (filtersRef.current.fromDate) {
                params.append('fromDate', filtersRef.current.fromDate);
            }
            if (filtersRef.current.toDate) {
                params.append('toDate', filtersRef.current.toDate);
            }

            const url = `${config.GETSUBSCRIPTIONCOUNTS}?${params.toString()}`;
            const response = await apiService.getSingle(url);
            const data = response?.data || response;
            setCounts(data);
        } catch (error) {
            console.error("Failed to fetch subscription counts:", error);
        }
    }, [user]);

    useEffect(() => {
        if (!authLoading && user) {
            filtersRef.current.searchTerm = "";
            filtersRef.current.companyId = null;
            filtersRef.current.planTypeFilter = "";
            filtersRef.current.statusFilter = "";
            filtersRef.current.fromDate = "";
            filtersRef.current.toDate = "";
            filtersRef.current.pageNumber = 1;
            setSelectedCompanyId(null);
            setFromDate("");
            setToDate("");
            getSubscriptions(1);
            fetchActivePlans();
            fetchAllClientsForSuggestions();
            fetchSubscriptionCounts();
        }
    }, [user, authLoading, getSubscriptions, fetchActivePlans, fetchAllClientsForSuggestions, fetchSubscriptionCounts]);

    const GetPageData = (number) => {
        if (number > 0 && number <= totalPages) {
            setPageNumber(number);
            filtersRef.current.pageNumber = number;
            if (pageNumber !== number) {
                getSubscriptions(number);
            }
        }
    };

    const renderPageNumbers = () => {
        const pageNumbers = [];
        for (let i = 1; i <= totalPages; i++) {
            pageNumbers.push(
                <li className={`page-item ${pageNumber === i ? "active" : ""}`} key={i}>
                    <Link to="#" className="page-link" onClick={() => GetPageData(i)}>{i}</Link>
                </li>
            );
        }
        return pageNumbers;
    };

    const formatLocalDateTime = (date) => {
        if (!date) return "";
        const tzoffset = date.getTimezoneOffset() * 60000;
        const localISOTime = (new Date(date - tzoffset)).toISOString().slice(0, 16);
        return localISOTime;
    };

    const handleViewDetails = (subscription) => {
        setSelectedSubscription(subscription);
        setViewShow(true);
    };

    const handleViewClose = () => {
        setViewShow(false);
        setSelectedSubscription(null);
    };

    // ==========================================
    // LOGIC PART 1: ADMINISTRATIVE EDIT POPUP
    // ==========================================
    const handleOpenEditModal = async (subscription) => {
        setSelectedClient({
            companyId: subscription.companyId,
            companyName: subscription.companyName,
            email: subscription.email,
            phoneNumber: subscription.phoneNumber
        });
        setActiveTab("subscription");

        setSubId(subscription.subscriptionId || "");

        const plan = availablePlans.find(p =>
            p.planType === subscription.planType &&
            p.billingCycle === subscription.billingCycle
        );
        setSubPlanId(plan ? plan.subscriptionPlanId : "");

        setRzpSubId(subscription.razorpaySubscriptionId || "");
        setRzpCustId(subscription.razorpayCustomerId || "");
        setSubStartDate(formatLocalDateTime(parseUTC(subscription.startDate)));
        setSubEndDate(formatLocalDateTime(parseUTC(subscription.endDate)));
        setSubCreatedDate(formatLocalDateTime(parseUTC(subscription.createdDate || new Date())));
        setSubStatus(subscription.status || "Active");

        let paymentData = null;
        try {
            const payUrl = `${config.GETSUBSCRIPTIONPAYMENTS}?companyId=${subscription.companyId}`;
            const payResponse = await apiService.getSingle(payUrl);
            const payments = payResponse?.data || payResponse || [];
            if (payments.length > 0) {
                paymentData = payments[0];
            }
        } catch (err) {
            console.warn("Failed to fetch past payments for editing:", err);
        }

        setSubPaymentId(paymentData?.subscriptionPaymentId || "");
        setRzpOrderId(paymentData?.razorpayOrderId || "");
        setRzpPaymentId(paymentData?.razorpayPaymentId || "");
        setPaymentAmount(paymentData?.amount || subscription.price || "");
        setSetupFee(paymentData?.setupFee || "0");
        setSubFee(paymentData?.subscriptionFee || "0");
        setPayStatus(paymentData?.status || "Success");
        setPayCreatedDate(formatLocalDateTime(parseUTC(paymentData?.createdDate || subscription.startDate || new Date())));
        setInvoiceNo(paymentData?.invoiceNo || "");

        setErrors({});
        setFormError("");
        setShowEditModal(true);
    };

    const handlePlanChange = (planId) => {
        setSubPlanId(planId);
        if (errors.subPlanId) {
            setErrors(prev => ({ ...prev, subPlanId: "" }));
        }

        const plan = availablePlans.find(p => p.subscriptionPlanId === planId);
        if (plan) {
            setPaymentAmount(plan.price);
            setSubFee(plan.price);
        } else {
            setPaymentAmount("");
            setSubFee("0");
        }
    };

    const validateEditForm = () => {
        const tempErrors = {};
        if (!subPlanId) tempErrors.subPlanId = "Subscription plan is required.";
        if (!subStartDate) tempErrors.subStartDate = "Start Date is required.";
        if (!subEndDate) tempErrors.subEndDate = "End Date is required.";
        if (subStartDate && subEndDate && new Date(subStartDate) >= new Date(subEndDate)) {
            tempErrors.subEndDate = "End Date must occur after the Start Date.";
        }

        const selectedPlan = availablePlans.find(p => p.subscriptionPlanId === subPlanId);
        const isTrialPlan = selectedPlan?.planType === "Trial";

        if (!isTrialPlan) {
            if (paymentAmount === "" || paymentAmount === undefined || parseFloat(paymentAmount) < 0) {
                tempErrors.paymentAmount = "Amount must be positive.";
            }
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleEditFormSubmit = async (e) => {
        e.preventDefault();
        setFormError("");

        if (!validateEditForm()) return;

        setSubmitting(true);
        try {
            const payload = {
                subscriptionId: subId,
                subscriptionPlanId: subPlanId,
                razorpaySubscriptionId: rzpSubId || null,
                razorpayCustomerId: rzpCustId || null,
                startDate: new Date(subStartDate).toISOString(),
                endDate: new Date(subEndDate).toISOString(),
                status: subStatus,
                createdDate: new Date(subCreatedDate).toISOString(),
                subscriptionPaymentId: subPaymentId || null,
                razorpayOrderId: rzpOrderId || null,
                razorpayPaymentId: rzpPaymentId || null,
                amount: parseFloat(paymentAmount) >= 0 ? parseFloat(paymentAmount) : null,
                setupFee: parseFloat(setupFee) >= 0 ? parseFloat(setupFee) : 0,
                subscriptionFee: parseFloat(subFee) >= 0 ? parseFloat(subFee) : 0,
                paymentStatus: payStatus,
                paymentCreatedDate: payCreatedDate ? new Date(payCreatedDate).toISOString() : null,
                invoiceNo: invoiceNo || null
            };

            await apiService.update(config.UPDATESUBSCRIPTION, payload);
            setShowEditModal(false);
            getSubscriptions(pageNumber);
            fetchSubscriptionCounts();
        } catch (error) {
            console.error("In-place update failed:", error);
            setFormError("Operation failed. Please verify API bindings.");
        } finally {
            setSubmitting(false);
        }
    };

    // ==========================================
    // LOGIC PART 2: MANUAL UPGRADE POPUP (7 PARAMS)
    // ==========================================
    const handleOpenUpgradeModal = (subscription) => {
        setSelectedClient({
            companyId: subscription.companyId,
            companyName: subscription.companyName,
            email: subscription.email,
            phoneNumber: subscription.phoneNumber
        });

        setUpgradePlanId("");
        setUpgradeRzpCustId(subscription.razorpayCustomerId || "");
        setUpgradeRzpSubId("");
        setUpgradeRzpPaymentId("");
        setUpgradePaymentDate(formatLocalDateTime(new Date()));
        setUpgradeAmount("");

        setErrors({});
        setFormError("");
        setShowUpgradeModal(true);
    };

    const handleUpgradePlanChange = (planId) => {
        setUpgradePlanId(planId);
        if (errors.upgradePlanId) {
            setErrors(prev => ({ ...prev, upgradePlanId: "" }));
        }

        const plan = availablePlans.find(p => p.subscriptionPlanId === planId);
        if (plan) {
            setUpgradeAmount(plan.price);
        } else {
            setUpgradeAmount("");
        }
    };

    const validateUpgradeForm = () => {
        const tempErrors = {};
        if (!upgradePlanId) tempErrors.upgradePlanId = "Please select a plan.";
        if (!upgradeRzpCustId.trim()) tempErrors.upgradeRzpCustId = "Customer ID is required.";
        if (!upgradeRzpSubId.trim()) tempErrors.upgradeRzpSubId = "Subscription ID is required.";
        if (!upgradeRzpPaymentId.trim()) tempErrors.upgradeRzpPaymentId = "Payment ID is required.";
        if (!upgradePaymentDate) tempErrors.upgradePaymentDate = "Payment date is required.";
        if (!upgradeAmount || parseFloat(upgradeAmount) < 0) {
            tempErrors.upgradeAmount = "Paid amount must be positive.";
        }
        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleUpgradeFormSubmit = async (e) => {
        e.preventDefault();
        setFormError("");

        if (!validateUpgradeForm()) return;

        setSubmitting(true);
        try {
            const payload = {
                companyId: selectedClient.companyId,
                subscriptionPlanId: upgradePlanId,
                razorpayPaymentId: upgradeRzpPaymentId.trim(),
                razorpaySubscriptionId: upgradeRzpSubId.trim(),
                razorpayCustomerId: upgradeRzpCustId.trim(),
                amount: parseFloat(upgradeAmount),
                paymentCreatedDate: new Date(upgradePaymentDate).toISOString()
            };

            await apiService.create(config.MANUALUPGRADE, payload);
            setShowUpgradeModal(false);
            getSubscriptions(pageNumber);
            fetchSubscriptionCounts();
        } catch (error) {
            console.error("Upgrade registration failed:", error);
            setFormError("Operation failed. Please verify endpoint parameters.");
        } finally {
            setSubmitting(false);
        }
    };

    // View transaction logs Modal
    const handleViewLogs = async (subscriptionId) => {
        setLoadingLogs(true);
        setShowLogsModal(true);
        try {
            const logsUrl = `${config.GETTRANSACTIONLOGS}/${subscriptionId}`;
            const response = await apiService.getSingle(logsUrl);
            setTransactionLogs(response || []);
        } catch (error) {
            console.error("Failed to load transaction logs:", error);
            setTransactionLogs([]);
        } finally {
            setLoadingLogs(false);
        }
    };

    // Fetch and display complete payment/billing history for a company
    const handleViewPayments = async (companyId) => {
        setLoadingPayments(true);
        setShowPaymentsModal(true);
        try {
            const payUrl = `${config.GETSUBSCRIPTIONPAYMENTS}?companyId=${companyId}`;
            const response = await apiService.getSingle(payUrl);
            const data = response?.data || response || [];
            setPaymentsList(data);
        } catch (error) {
            console.error("Failed to load payment history logs:", error);
            setPaymentsList([]);
        } finally {
            setLoadingPayments(false);
        }
    };

    const handleSendActivationEmail = async (subscription) => {
        setSendingEmailId(subscription.companyId);
        try {
            const url = `${config.SENDSUBSCRIPTIONACTIVEMAIL}/${subscription.companyId}`;
            await apiService.create(url, {});
        } catch (error) {
            console.error("Email dispatch failed:", error);
        } finally {
            setSendingEmailId(null);
        }
    };

    const handleOpenDeleteModal = (subscriptionId) => {
        setItemToDelete(subscriptionId);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setItemToDelete(null);
    };

    const handleDelete = async () => {
        if (itemToDelete !== null) {
            try {
                await apiService.remove(config.DELETESUBSCRIPTION, itemToDelete);
                await getSubscriptions(pageNumber);
                await fetchSubscriptionCounts();
            } catch (error) {
                console.error("Failed to delete subscription:", error);
            }
            handleCloseDeleteModal();
        }
    };

    const generateSuggestions = (searchValue) => {
        if (!searchValue || !searchValue.trim()) {
            setSuggestions([]);
            return;
        }

        const searchLower = searchValue.toLowerCase();

        const companyMatches = allClients
            .filter((client) => client.companyName?.toLowerCase().includes(searchLower)).slice(0, 5);
        const brandMatches = allClients
            .filter((client) => client.brandName?.toLowerCase().includes(searchLower) && !client.companyName?.toLowerCase().includes(searchLower)).slice(0, 3);
        const emailMatches = allClients
            .filter((client) => client.email?.toLowerCase().includes(searchLower) && !client.companyName?.toLowerCase().includes(searchLower) && !client.brandName?.toLowerCase().includes(searchLower)).slice(0, 2);

        setSuggestions([...companyMatches, ...brandMatches, ...emailMatches]);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setShowSuggestions(true);
        generateSuggestions(value);
    };

    const handleSearchButtonClick = () => {
        if (!searchTerm.trim()) {
            setSelectedCompanyId(null);
            filtersRef.current.companyId = null;
        }
        filtersRef.current.searchTerm = searchTerm;
        filtersRef.current.planTypeFilter = selectedPlanType;
        filtersRef.current.statusFilter = selectedStatus;
        filtersRef.current.fromDate = fromDate;
        filtersRef.current.toDate = toDate;
        filtersRef.current.pageNumber = 1;
        setPageNumber(1);
        getSubscriptions(1);
        fetchSubscriptionCounts();
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearchButtonClick();
        }
    };

    const handleSuggestionClick = (client) => {
        setSearchTerm(client.companyName);
        setSelectedCompanyId(client.companyId);
        filtersRef.current.companyId = client.companyId;
        setShowSuggestions(false);
        setSuggestions([]);
        filtersRef.current.searchTerm = client.companyName;
        filtersRef.current.planTypeFilter = selectedPlanType;
        filtersRef.current.statusFilter = selectedStatus;
        filtersRef.current.fromDate = fromDate;
        filtersRef.current.toDate = toDate;
        filtersRef.current.pageNumber = 1;
        setPageNumber(1);
        getSubscriptions(1);
        fetchSubscriptionCounts();
    };

    const handleInputBlur = () => {
        setTimeout(() => setShowSuggestions(false), 200);
    };

    const clearSearch = () => {
        setSearchTerm("");
        setSelectedCompanyId(null);
        filtersRef.current.companyId = null;
    };

    const handlePlanTypeChange = (e) => setSelectedPlanType(e.target.value);
    const handleStatusChange = (e) => setSelectedStatus(e.target.value);
    const handleFromDateChange = (e) => setFromDate(e.target.value);
    const handleToDateChange = (e) => setToDate(e.target.value);

    const handleResetFilters = () => {
        setSearchTerm("");
        setSelectedPlanType("");
        setSelectedStatus("");
        setFromDate("");
        setToDate("");
        setSelectedCompanyId(null);
        setShowSuggestions(false);
        setSuggestions([]);
        filtersRef.current.searchTerm = "";
        filtersRef.current.companyId = null;
        filtersRef.current.planTypeFilter = "";
        filtersRef.current.statusFilter = "";
        filtersRef.current.fromDate = "";
        filtersRef.current.toDate = "";
        filtersRef.current.pageNumber = 1;
        setPageNumber(1);
        getSubscriptions(1);
        fetchSubscriptionCounts();
    };

    const getSubscriptionStatusBadgeColor = (status) => {
        const colors = {
            'Active': 'bg-success',
            'Pending': 'bg-warning text-dark',
            'Suspended': 'bg-danger',
            'Cancelled': 'bg-secondary',
            'Expired': 'bg-dark',
            'CancelRequested': 'bg-info'
        };
        return colors[status] || 'bg-secondary';
    };

    const getPlanTypeBadgeColor = (planType) => {
        const colors = {
            'Micro': 'bg-warning',
            'Trial': 'bg-info',
            'Starter': 'bg-primary',
            'Basic': 'bg-success',
        };
        return colors[planType] || 'bg-secondary';
    };

    const renderSubscriptionDetails = () => {
        if (!selectedSubscription) return null;

        const subscriptionStatusClass = getSubscriptionStatusBadgeColor(selectedSubscription.status);

        return (
            <div className="container-fluid">
                <div className={`alert ${selectedSubscription.status === 'Active' ? 'alert-success' : 'alert-warning'} mb-4`}>
                    <i className={`ri-${selectedSubscription.status === 'Active' ? 'check-circle' : 'alert-circle'}-line me-2`}></i>
                    <strong>Status: {selectedSubscription.status}</strong>
                </div>

                <div className="row mb-3">
                    <div className="col-12"><h6 className="border-bottom pb-2 mb-3 text-primary">Subscription Information</h6></div>
                </div>

                <div className="row mb-2">
                    <div className="col-md-6"><strong>Company Name:</strong></div>
                    <div className="col-md-6">{selectedSubscription.companyName || '-'}</div>
                </div>
                <div className="row mb-2">
                    <div className="col-md-6"><strong>Email:</strong></div>
                    <div className="col-md-6">{selectedSubscription.email || '-'}</div>
                </div>
                <div className="row mb-2">
                    <div className="col-md-6"><strong>Phone Number:</strong></div>
                    <div className="col-md-6">{selectedSubscription.phoneNumber || '-'}</div>
                </div>
                <div className="row mb-2">
                    <div className="col-md-6"><strong>Plan Name:</strong></div>
                    <div className="col-md-6">{selectedSubscription.planName || '-'}</div>
                </div>

                <div className="row mb-2">
                    <div className="col-md-6"><strong>Plan Type:</strong></div>
                    <div className="col-md-6">
                        <span className={`badge ${getPlanTypeBadgeColor(selectedSubscription.planType)}`}>
                            {selectedSubscription.planType || '-'}
                        </span>
                    </div>
                </div>

                <div className="row mb-2">
                    <div className="col-md-6"><strong>Price:</strong></div>
                    <div className="col-md-6"><span className="fw-bold">INR {selectedSubscription.price || 0}</span></div>
                </div>

                <div className="row mb-2">
                    <div className="col-md-6"><strong>Billing Cycle:</strong></div>
                    <div className="col-md-6"><span className="badge bg-primary">{selectedSubscription.billingCycle || '-'}</span></div>
                </div>

                <div className="row mb-2">
                    <div className="col-md-6"><strong>Status:</strong></div>
                    <div className="col-md-6">
                        <span className={`badge ${subscriptionStatusClass}`}>
                            {selectedSubscription.status}
                        </span>
                    </div>
                </div>

                <div className="row mb-3 mt-3">
                    <div className="col-12"><h6 className="border-bottom pb-2 mb-3 text-primary">Dates</h6></div>
                </div>

                <div className="row mb-2">
                    <div className="col-md-6"><strong>Start Date:</strong></div>
                    <div className="col-md-6">{formatDate(selectedSubscription.startDate)}</div>
                </div>

                <div className="row mb-2">
                    <div className="col-md-6"><strong>End Date:</strong></div>
                    <div className="col-md-6">{formatDate(selectedSubscription.endDate)}</div>
                </div>

                <div className="row mb-2">
                    <div className="col-md-6"><strong>Created Date:</strong></div>
                    <div className="col-md-6">{formatDate(selectedSubscription.createdDate)}</div>
                </div>

                {selectedSubscription.razorpaySubscriptionId && (
                    <>
                        <div className="row mb-3 mt-3">
                            <div className="col-12"><h6 className="border-bottom pb-2 mb-3 text-primary">Payment Details</h6></div>
                        </div>

                        <div className="row mb-2">
                            <div className="col-md-6"><strong>Razorpay Subscription ID:</strong></div>
                            <div className="col-md-6"><code>{selectedSubscription.razorpaySubscriptionId}</code></div>
                        </div>

                        <div className="row mb-2">
                            <div className="col-md-6"><strong>Razorpay Customer ID:</strong></div>
                            <div className="col-md-6"><code>{selectedSubscription.razorpayCustomerId}</code></div>
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <>
            <Header />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="card">
                            <div className="p-2">
                                <SubscriptionTabs pageName="CLIENT SUBSCRIPTIONS" />
                            </div>

                            <div className="card-body">
                                {/* Compact Count Cards - Single Row */}
                                <div className="row g-2 mb-3">
                                    {/* Left side - Status Counts */}
                                    <div className="col-md-6">
                                        <div className="d-flex flex-wrap gap-2">
                                            {/* <div className="card bg-primary text-white" style={{ flex: '1 0 auto', minWidth: '70px' }}>
                                                <div className="card-body py-1 px-2 text-center">
                                                    <small className="text-dark-50 d-block" style={{ fontSize: '9px' }}>Total</small>
                                                    <h6 className="text-white mb-0">{counts.totalSubscriptions}</h6>
                                                </div>
                                            </div>
                                            <div className="card bg-success text-white" style={{ flex: '1 0 auto', minWidth: '70px' }}>
                                                <div className="card-body py-1 px-2 text-center">
                                                    <small className="text-dark-50 d-block" style={{ fontSize: '9px' }}>Active</small>
                                                    <h6 className="text-white mb-0">{counts.activeCount}</h6>
                                                </div>
                                            </div>
                                            <div className="card bg-warning text-white" style={{ flex: '1 0 auto', minWidth: '70px' }}>
                                                <div className="card-body py-1 px-2 text-center">
                                                    <small className="text-dark-50 d-block" style={{ fontSize: '9px' }}>Pending</small>
                                                    <h6 className="text-white mb-0">{counts.pendingCount}</h6>
                                                </div>
                                            </div>
                                            <div className="card bg-danger text-white" style={{ flex: '1 0 auto', minWidth: '70px' }}>
                                                <div className="card-body py-1 px-2 text-center">
                                                    <small className="text-dark-50 d-block" style={{ fontSize: '9px' }}>Suspended</small>
                                                    <h6 className="text-white mb-0">{counts.suspendedCount}</h6>
                                                </div>
                                            </div> */}
                                            <div className="card bg-secondary text-white" style={{ flex: '1 0 auto', minWidth: '70px' }}>
                                                <div className="card-body py-1 px-2 text-center">
                                                    <small className="text-dark-50 d-block" style={{ fontSize: '9px' }}>Cancelled</small>
                                                    <h6 className="text-white mb-0">{counts.cancelledCount}</h6>
                                                </div>
                                            </div>
                                            <div className="card bg-dark text-white" style={{ flex: '1 0 auto', minWidth: '70px' }}>
                                                <div className="card-body py-1 px-2 text-center">
                                                    <small className="text-dark-50 d-block" style={{ fontSize: '9px' }}>Expired</small>
                                                    <h6 className="text-white mb-0">{counts.expiredCount}</h6>
                                                </div>
                                            </div>
                                            {/* <div className="card bg-info text-white" style={{ flex: '1 0 auto', minWidth: '70px' }}>
                                                <div className="card-body py-1 px-2 text-center">
                                                    <small className="text-dark-50 d-block" style={{ fontSize: '9px' }}>Cancel Req</small>
                                                    <h6 className="text-white mb-0">{counts.cancelRequestedCount}</h6>
                                                </div>
                                            </div> */}
                                        </div>
                                    </div>

                                    {/* Right side - Plan Type Counts */}
                                    <div className="col-md-6">
                                        <div className="d-flex flex-wrap gap-2 justify-content-end">
                                            <div className="card border border-warning" style={{ flex: '1 0 auto', minWidth: '70px' }}>
                                                <div className="card-body py-1 px-2 text-center">
                                                    <span className="badge bg-warning d-block" style={{ fontSize: '8px' }}>MICRO</span>
                                                    <h6 className="mb-0">{counts.microCount}</h6>
                                                </div>
                                            </div>
                                            <div className="card border border-info" style={{ flex: '1 0 auto', minWidth: '70px' }}>
                                                <div className="card-body py-1 px-2 text-center">
                                                    <span className="badge bg-info d-block" style={{ fontSize: '8px' }}>TRIAL</span>
                                                    <h6 className="mb-0">{counts.trialCount}</h6>
                                                </div>
                                            </div>
                                            <div className="card border border-success" style={{ flex: '1 0 auto', minWidth: '70px' }}>
                                                <div className="card-body py-1 px-2 text-center">
                                                    <span className="badge bg-success d-block" style={{ fontSize: '8px' }}>BASIC</span>
                                                    <h6 className="mb-0">{counts.basicCount}</h6>
                                                </div>
                                            </div>
                                            <div className="card border border-primary" style={{ flex: '1 0 auto', minWidth: '70px' }}>
                                                <div className="card-body py-1 px-2 text-center">
                                                    <span className="badge bg-primary d-block" style={{ fontSize: '8px' }}>STARTER</span>
                                                    <h6 className="mb-0">{counts.starterCount}</h6>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Search and Filter Area */}
                                <div className="row align-items-end mb-4 g-3">
                                    <div className="col-md-4">
                                        <label className="form-label">Search Company</label>
                                        <div style={{ position: "relative" }}>
                                            <div className="input-group" style={{ height: "45px" }}>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search by company name, brand name, or email..."
                                                    value={searchTerm}
                                                    onChange={handleSearchChange}
                                                    onKeyPress={handleSearchKeyPress}
                                                    onFocus={() => setShowSuggestions(true)}
                                                    onBlur={handleInputBlur}
                                                    style={{ height: "45px", paddingLeft: "40px" }}
                                                />
                                                <i className="ri-search-line" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", zIndex: 10, color: "#6c757d" }}></i>
                                                {searchTerm && (
                                                    <button className="btn position-absolute end-0 top-0 bottom-0" style={{ background: "transparent", border: "none", zIndex: 10 }} onClick={clearSearch}>
                                                        <i className="ri-close-line fs-5"></i>
                                                    </button>
                                                )}
                                            </div>

                                            {showSuggestions && suggestions.length > 0 && (
                                                <div style={{ position: "absolute", width: "100%", marginTop: "4px", border: "1px solid #dee2e6", backgroundColor: "white", borderRadius: "6px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", zIndex: 1000 }}>
                                                    {suggestions.map((client) => (
                                                        <div
                                                            key={client.companyId}
                                                            style={{ padding: "8px 12px", display: "flex", alignItems: "center", cursor: "pointer", backgroundColor: "white" }}
                                                            onMouseEnter={(e) => { e.target.style.backgroundColor = "#f8f9fa"; }}
                                                            onMouseLeave={(e) => { e.target.style.backgroundColor = "white"; }}
                                                            onMouseDown={(e) => { e.preventDefault(); handleSuggestionClick(client); }}
                                                        >
                                                            <i className="ri-building-2-line me-2 text-secondary"></i>
                                                            <div>
                                                                <div style={{ fontWeight: "600" }}>{client.companyName}</div>
                                                                {client.brandName && client.brandName !== client.companyName && (
                                                                    <div style={{ fontSize: "0.875rem", color: "#6c757d" }}>{client.brandName}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-md-2">
                                        <label className="form-label">Plan Type</label>
                                        <select className="form-select" value={selectedPlanType} onChange={handlePlanTypeChange} style={{ height: "45px" }}>
                                            <option value="">All Plans</option>
                                            {planTypes.map((plan) => (
                                                <option key={plan} value={plan}>{plan}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-md-2">
                                        <label className="form-label">Status</label>
                                        <select className="form-select" value={selectedStatus} onChange={handleStatusChange} style={{ height: "45px" }}>
                                            <option value="">All Status</option>
                                            <option value="Active">Active</option>
                                            <option value="Pending">Pending</option>
                                            <option value="Suspended">Suspended</option>
                                            <option value="Cancelled">Cancelled</option>
                                            <option value="Expired">Expired</option>
                                            <option value="CancelRequested">Cancel Requested</option>
                                        </select>
                                    </div>

                                    <div className="col-md-2">
                                        <label className="form-label">From Date</label>
                                        <input type="date" className="form-control" value={fromDate} onChange={handleFromDateChange} style={{ height: "45px" }} />
                                    </div>

                                    <div className="col-md-2">
                                        <label className="form-label">To Date</label>
                                        <input type="date" className="form-control" value={toDate} onChange={handleToDateChange} style={{ height: "45px" }} />
                                    </div>

                                    <div className="col-md-12">
                                        <div className="d-flex gap-2">
                                            <button className="btn btn-primary" onClick={handleSearchButtonClick} style={{ height: "45px", minWidth: "120px" }}>
                                                <i className="ri-search-line me-1"></i> Search
                                            </button>
                                            <button className="btn btn-danger" onClick={handleResetFilters} style={{ height: "45px", minWidth: "120px" }}>
                                                <i className="ri-refresh-line me-1"></i> Reset
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="table-responsive table-card mt-4">
                                    <table className="table align-middle table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Company</th>
                                                <th>Plan</th>
                                                <th>Price</th>
                                                <th>Billing Cycle</th>
                                                <th>Status</th>
                                                <th>Start Date</th>
                                                <th>End Date</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan="9" className="text-center py-4">
                                                        <div className="spinner-border text-primary" role="status"></div>
                                                        <p className="mt-2 text-muted">Loading subscriptions...</p>
                                                    </td>
                                                </tr>
                                            ) : subscriptions.length > 0 ? (
                                                subscriptions.map((subscription, index) => {
                                                    const statusClass = getSubscriptionStatusBadgeColor(subscription.status);
                                                    const planTypeClass = getPlanTypeBadgeColor(subscription.planType);

                                                    return (
                                                        <tr key={subscription.subscriptionId || index}>
                                                            <td>{((pageNumber - 1) * pageSize) + index + 1}</td>
                                                            <td>
                                                                <div><strong>{subscription.companyName || '-'}</strong></div>
                                                                <small className="text-muted">{subscription.email || '-'}</small>
                                                            </td>
                                                            <td><span className={`badge ${planTypeClass}`}>{subscription.planType || '-'}</span></td>
                                                            <td><span className="fw-bold">INR {subscription.price || 0}</span></td>
                                                            <td><span className="badge bg-primary">{subscription.billingCycle || '-'}</span></td>
                                                            <td><span className={`badge ${statusClass}`}>{subscription.status}</span></td>
                                                            <td>{formatDate(subscription.startDate)}</td>
                                                            <td>{formatDate(subscription.endDate)}</td>
                                                            <td>
                                                                <div className="d-flex gap-2 flex-wrap">
                                                                    <button className="btn btn-sm btn-info d-flex align-items-center gap-1" onClick={() => handleViewDetails(subscription)}>
                                                                        <i className="ri-eye-line"></i>
                                                                    </button>
                                                                    <button className="btn btn-sm btn-primary d-flex align-items-center gap-1" onClick={() => handleOpenEditModal(subscription)}>
                                                                        <i className="ri-edit-line"></i> Edit
                                                                    </button>
                                                                    <button className="btn btn-sm btn-warning d-flex align-items-center gap-1" onClick={() => handleOpenUpgradeModal(subscription)}>
                                                                        <i className="ri-arrow-up-circle-line"></i> Manual
                                                                    </button>
                                                                    <button className="btn btn-sm btn-success d-flex align-items-center gap-1" onClick={() => handleSendActivationEmail(subscription)} disabled={sendingEmailId === subscription.companyId}>
                                                                        {sendingEmailId === subscription.companyId ? "Sending..." : "Send Mail"}
                                                                    </button>
                                                                    <button className="btn btn-sm btn-dark d-flex align-items-center gap-1" onClick={() => handleViewLogs(subscription.subscriptionId)}>
                                                                        <i className="ri-history-line"></i> Logs
                                                                    </button>
                                                                    <button className="btn btn-sm btn-success d-flex align-items-center gap-1" onClick={() => handleViewPayments(subscription.companyId)}>
                                                                        <i className="ri-wallet-3-line"></i> Payments
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm btn-soft-danger d-flex align-items-center gap-1"
                                                                        onClick={() => handleOpenDeleteModal(subscription.companyId)}
                                                                    >
                                                                        <i className="ri-delete-bin-line"></i>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="9" className="text-center py-4">No subscriptions found.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {subscriptions.length > 0 && (
                                    <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                                        <div className="flex-shrink-0">
                                            <div className="text-muted">
                                                Showing <span className="fw-semibold">{subscriptions.length}</span> of <span className="fw-semibold">{totalRecords}</span> Results
                                            </div>
                                        </div>
                                        <ul className="pagination pagination-separated pagination-sm mb-0">
                                            <li className={`page-item ${pageNumber === 1 ? "disabled" : ""}`}>
                                                <Link to="#" className="page-link" onClick={() => GetPageData(pageNumber - 1)}>Previous</Link>
                                            </li>
                                            {renderPageNumbers()}
                                            <li className={`page-item ${pageNumber === totalPages ? "disabled" : ""}`}>
                                                <Link to="#" className="page-link" onClick={() => GetPageData(pageNumber + 1)}>Next</Link>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* View Details Modal */}
            <Modal show={viewShow} onHide={handleViewClose} size="lg" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title><i className="ri-file-list-3-line me-2 text-primary"></i>Subscription Details</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    {renderSubscriptionDetails()}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-danger" onClick={handleViewClose}>Close</button>
                </Modal.Footer>
            </Modal>

            {/* Transaction Logs Modal */}
            <Modal show={showLogsModal} onHide={() => setShowLogsModal(false)} size="lg" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title><i className="ri-history-line me-2 text-primary"></i>Subscription Transaction History</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingLogs ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status"></div>
                            <p className="mt-2 text-muted">Retrieving audit history logs...</p>
                        </div>
                    ) : transactionLogs.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-bordered table-striped align-middle fs-13">
                                <thead className="table-light">
                                    <tr>
                                        <th>Event Type</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                        <th>Payment ID</th>
                                        <th>Subscription ID</th>
                                        <th>Timestamp</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactionLogs.map((log) => (
                                        <tr key={log.transactionLogId}>
                                            <td><span className="badge bg-soft-info text-info">{log.eventType}</span></td>
                                            <td>INR {log.amount}</td>
                                            <td><span className={`badge ${log.status === 'Success' ? 'bg-success' : 'bg-danger'}`}>{log.status}</span></td>
                                            <td><code>{log.razorpayPaymentId || '-'}</code></td>
                                            <td><code>{log.razorpaySubscriptionId || '-'}</code></td>
                                            <td>{formatDate(log.occurredAtUtc)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-4 text-muted">
                            <i className="ri-history-line fs-1 d-block mb-2"></i>
                            No transaction audit logs recorded for this subscriber yet.
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={() => setShowLogsModal(false)}>Close</button>
                </Modal.Footer>
            </Modal>

            {/* Payments History Modal */}
            <Modal show={showPaymentsModal} onHide={() => setShowPaymentsModal(false)} size="xl" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="ri-wallet-3-line me-2 text-primary"></i>
                        Billing & Payment History Ledger
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingPayments ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status"></div>
                            <p className="mt-2 text-muted">Retrieving complete payment logs...</p>
                        </div>
                    ) : paymentsList.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-bordered table-striped align-middle fs-13">
                                <thead className="table-light">
                                    <tr>
                                        <th>Invoice No</th>
                                        <th>Plan Type</th>
                                        <th>Setup Fee</th>
                                        <th>Sub Fee</th>
                                        <th>Total Paid</th>
                                        <th>Payment Status</th>
                                        <th>Payment ID</th>
                                        <th>Order ID</th>
                                        <th>Checkout Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paymentsList.map((payment) => (
                                        <tr key={payment.subscriptionPaymentId}>
                                            <td><strong>{payment.invoiceNo || '-'}</strong></td>
                                            <td>
                                                <span className={`badge ${getPlanTypeBadgeColor(payment.planType)}`}>
                                                    {payment.planType || '-'}
                                                </span>
                                            </td>
                                            <td>INR {payment.setupFee || 0}</td>
                                            <td>INR {payment.subscriptionFee || 0}</td>
                                            <td className="fw-semibold">INR {payment.amount || 0}</td>
                                            <td>
                                                <span className={`badge ${payment.status === 'Success' ? 'bg-success' : 'bg-danger'}`}>
                                                    {payment.status || '-'}
                                                </span>
                                            </td>
                                            <td><code>{payment.razorpayPaymentId || '-'}</code></td>
                                            <td><code>{payment.razorpayOrderId || '-'}</code></td>
                                            <td>{formatDate(payment.createdDate)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-4 text-muted">
                            <i className="ri-wallet-3-line fs-1 d-block mb-2"></i>
                            No payment transactions recorded for this company.
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={() => setShowPaymentsModal(false)}>Close</button>
                </Modal.Footer>
            </Modal>

            {/* MODAL 1: Administrative In-Place Edit */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="ri-edit-line me-2 text-primary"></i>
                        Edit Client Subscription & Payment
                    </Modal.Title>
                </Modal.Header>

                <div className="px-3 pt-2 bg-light border-bottom">
                    <ul className="nav nav-tabs border-bottom-0">
                        <li className="nav-item">
                            <button className={`nav-link fw-semibold ${activeTab === 'subscription' ? 'active' : ''}`} onClick={() => setActiveTab('subscription')}>
                                <i className="ri-calendar-todo-line me-1"></i> Subscription Details
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className={`nav-link fw-semibold ${activeTab === 'payment' ? 'active' : ''}`} onClick={() => setActiveTab('payment')}>
                                <i className="ri-wallet-3-line me-1"></i> Payment Details
                            </button>
                        </li>
                    </ul>
                </div>

                <form onSubmit={handleEditFormSubmit} noValidate>
                    <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
                        {formError && (
                            <div className="alert alert-danger py-2 fs-13" role="alert">
                                <i className="ri-error-warning-line me-1 align-middle"></i> {formError}
                            </div>
                        )}

                        <div className="row">
                            <div className="col-md-12 mb-3">
                                <label className="form-label fw-semibold">Client / Company Name</label>
                                <input type="text" className="form-control" value={selectedClient?.companyName || ""} readOnly disabled />
                            </div>
                        </div>

                        {activeTab === "subscription" && (
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label">Select Active Billing Plan</label>
                                    <select
                                        className={`form-select ${errors.subPlanId ? 'is-invalid' : ''}`}
                                        value={subPlanId}
                                        onChange={(e) => handlePlanChange(e.target.value)}
                                        required
                                    >
                                        <option value="">-- Choose Plan --</option>
                                        {availablePlans.map(plan => (
                                            <option key={plan.subscriptionPlanId} value={plan.subscriptionPlanId}>
                                                {plan.planType} ({plan.billingCycle}) - INR {plan.price}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.subPlanId && <div className="invalid-feedback">{errors.subPlanId}</div>}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Subscription Status</label>
                                    <select className="form-select" value={subStatus} onChange={(e) => setSubStatus(e.target.value)}>
                                        <option value="Active">Active</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Suspended">Suspended</option>
                                        <option value="Cancelled">Cancelled</option>
                                        <option value="CancelRequested">Cancel Requested</option>
                                        <option value="Trial">Trial</option>
                                    </select>
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Razorpay Subscription ID</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.rzpSubId ? 'is-invalid' : ''}`}
                                        placeholder="sub_xxxxxx"
                                        value={rzpSubId}
                                        onChange={(e) => setRzpSubId(e.target.value)}
                                    />
                                    {errors.rzpSubId && <div className="invalid-feedback">{errors.rzpSubId}</div>}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Razorpay Customer ID</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.rzpCustId ? 'is-invalid' : ''}`}
                                        placeholder="cust_xxxxxx"
                                        value={rzpCustId}
                                        onChange={(e) => setRzpCustId(e.target.value)}
                                    />
                                    {errors.rzpCustId && <div className="invalid-feedback">{errors.rzpCustId}</div>}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Subscription Start Date</label>
                                    <input
                                        type="datetime-local"
                                        className={`form-control ${errors.subStartDate ? 'is-invalid' : ''}`}
                                        value={subStartDate}
                                        onChange={(e) => setSubStartDate(e.target.value)}
                                        required
                                    />
                                    {errors.subStartDate && <div className="invalid-feedback">{errors.subStartDate}</div>}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Subscription End Date</label>
                                    <input
                                        type="datetime-local"
                                        className={`form-control ${errors.subEndDate ? 'is-invalid' : ''}`}
                                        value={subEndDate}
                                        onChange={(e) => setSubEndDate(e.target.value)}
                                        required
                                    />
                                    {errors.subEndDate && <div className="invalid-feedback">{errors.subEndDate}</div>}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Created Date (Subscription)</label>
                                    <input
                                        type="datetime-local"
                                        className="form-control"
                                        value={subCreatedDate}
                                        onChange={(e) => setSubCreatedDate(e.target.value)}
                                    />
                                </div>
                            </div>
                        )}

                        {activeTab === "payment" && (
                            <div>
                                {(!subPaymentId && (subPlanId === "" || availablePlans.find(p => p.subscriptionPlanId === subPlanId)?.planType === "Trial")) ? (
                                    <div className="alert alert-warning text-center p-4 mb-0" role="alert">
                                        <i className="ri-information-line fs-24 d-block mb-2 text-warning"></i>
                                        <h6 className="fw-bold">No Active Payment Record</h6>
                                        <p className="text-muted mb-0 fs-13">
                                            Trial subscriptions do not have associated payment ledger transactions.
                                            To assign a paid plan (Basic, Starter, etc.) and record a payment transaction,
                                            please close this modal and use the <strong className="text-primary">Upgrade</strong> action instead.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Razorpay Order ID</label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.rzpOrderId ? 'is-invalid' : ''}`}
                                                placeholder="order_xxxxxx"
                                                value={rzpOrderId}
                                                onChange={(e) => setRzpOrderId(e.target.value)}
                                            />
                                            {errors.rzpOrderId && <div className="invalid-feedback">{errors.rzpOrderId}</div>}
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Razorpay Payment ID</label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.rzpPaymentId ? 'is-invalid' : ''}`}
                                                placeholder="pay_xxxxxx"
                                                value={rzpPaymentId}
                                                onChange={(e) => setRzpPaymentId(e.target.value)}
                                            />
                                            {errors.rzpPaymentId && <div className="invalid-feedback">{errors.rzpPaymentId}</div>}
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Invoice Number</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="INV-YYYYMMDD-XXXX"
                                                value={invoiceNo}
                                                onChange={(e) => setInvoiceNo(e.target.value)}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Payment Status</label>
                                            <select className="form-select" value={payStatus} onChange={(e) => setPayStatus(e.target.value)}>
                                                <option value="Success">Success</option>
                                                <option value="Failed">Failed</option>
                                                <option value="Pending">Pending</option>
                                            </select>
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Total Amount Paid (INR)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={`form-control ${errors.paymentAmount ? 'is-invalid' : ''}`}
                                                placeholder="0.00"
                                                value={paymentAmount}
                                                onChange={(e) => setPaymentAmount(e.target.value)}
                                                required
                                            />
                                            {errors.paymentAmount && <div className="invalid-feedback">{errors.paymentAmount}</div>}
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Setup Fee (INR)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className="form-control"
                                                value={setupFee}
                                                onChange={(e) => setSetupFee(e.target.value)}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Subscription Fee Portion (INR)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className="form-control"
                                                value={subFee}
                                                onChange={(e) => setSubFee(e.target.value)}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Payment Checkout Date</label>
                                            <input
                                                type="datetime-local"
                                                className="form-control"
                                                value={payCreatedDate}
                                                onChange={(e) => setPayCreatedDate(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <button type="button" className="btn btn-light" onClick={() => setShowEditModal(false)}>Close</button>
                        <button type="submit" className="btn btn-primary" disabled={submitting}>
                            {submitting ? "Processing..." : "Save Changes"}
                        </button>
                    </Modal.Footer>
                </form>
            </Modal>

            {/* MODAL 2: Simple Manual Subscription Upgrade */}
            <Modal show={showUpgradeModal} onHide={() => setShowUpgradeModal(false)} size="md" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold text-dark">
                        Manual Subscription Upgrade
                    </Modal.Title>
                </Modal.Header>
                <form onSubmit={handleUpgradeFormSubmit} noValidate>
                    <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                        {formError && (
                            <div className="alert alert-danger py-2 fs-13" role="alert">
                                <i className="ri-error-warning-line me-1 align-middle"></i> {formError}
                            </div>
                        )}

                        <div className="mb-3">
                            <label className="form-label fw-semibold">Client Name</label>
                            <input
                                type="text"
                                className="form-control bg-light"
                                value={selectedClient?.companyName || ""}
                                readOnly
                                disabled
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-semibold">Select Plan Type</label>
                            <select
                                className={`form-select ${errors.upgradePlanId ? 'is-invalid' : ''}`}
                                value={upgradePlanId}
                                onChange={(e) => handleUpgradePlanChange(e.target.value)}
                                required
                            >
                                <option value="">-- Choose Plan --</option>
                                {availablePlans.map(plan => (
                                    <option key={plan.subscriptionPlanId} value={plan.subscriptionPlanId}>
                                        {plan.planType} ({plan.billingCycle}) - INR {plan.price}
                                    </option>
                                ))}
                            </select>
                            {errors.upgradePlanId && <div className="invalid-feedback">{errors.upgradePlanId}</div>}
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-semibold">Razorpay Customer ID (cust_xxxx)</label>
                            <input
                                type="text"
                                className={`form-control ${errors.upgradeRzpCustId ? 'is-invalid' : ''}`}
                                placeholder="Enter Customer ID from Razorpay Console"
                                value={upgradeRzpCustId}
                                onChange={(e) => {
                                    setUpgradeRzpCustId(e.target.value);
                                    if (errors.upgradeRzpCustId) setErrors(prev => ({ ...prev, upgradeRzpCustId: "" }));
                                }}
                                required
                            />
                            {errors.upgradeRzpCustId && <div className="invalid-feedback">{errors.upgradeRzpCustId}</div>}
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-semibold">Razorpay Subscription ID (sub_xxxx)</label>
                            <input
                                type="text"
                                className={`form-control ${errors.upgradeRzpSubId ? 'is-invalid' : ''}`}
                                placeholder="Enter Subscription ID from Razorpay Console"
                                value={upgradeRzpSubId}
                                onChange={(e) => {
                                    setUpgradeRzpSubId(e.target.value);
                                    if (errors.upgradeRzpSubId) setErrors(prev => ({ ...prev, upgradeRzpSubId: "" }));
                                }}
                                required
                            />
                            {errors.upgradeRzpSubId && <div className="invalid-feedback">{errors.upgradeRzpSubId}</div>}
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-semibold">Razorpay Payment ID (pay_xxxx)</label>
                            <input
                                type="text"
                                className={`form-control ${errors.upgradeRzpPaymentId ? 'is-invalid' : ''}`}
                                placeholder="Enter Transaction Payment ID"
                                value={upgradeRzpPaymentId}
                                onChange={(e) => {
                                    setUpgradeRzpPaymentId(e.target.value);
                                    if (errors.upgradeRzpPaymentId) setErrors(prev => ({ ...prev, upgradeRzpPaymentId: "" }));
                                }}
                                required
                            />
                            {errors.upgradeRzpPaymentId && <div className="invalid-feedback">{errors.upgradeRzpPaymentId}</div>}
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-semibold">Payment Date / Time (Razorpay Checkout Date)</label>
                            <input
                                type="datetime-local"
                                className={`form-control ${errors.upgradePaymentDate ? 'is-invalid' : ''}`}
                                value={upgradePaymentDate}
                                onChange={(e) => {
                                    setUpgradePaymentDate(e.target.value);
                                    if (errors.upgradePaymentDate) setErrors(prev => ({ ...prev, upgradePaymentDate: "" }));
                                }}
                                required
                            />
                            {errors.upgradePaymentDate && <div className="invalid-feedback">{errors.upgradePaymentDate}</div>}
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-semibold">Amount Paid (INR)</label>
                            <input
                                type="number"
                                step="0.01"
                                className={`form-control ${errors.upgradeAmount ? 'is-invalid' : ''}`}
                                placeholder="0.00"
                                value={upgradeAmount}
                                onChange={(e) => {
                                    setUpgradeAmount(e.target.value);
                                    if (errors.upgradeAmount) setErrors(prev => ({ ...prev, upgradeAmount: "" }));
                                }}
                                required
                            />
                            {errors.upgradeAmount && <div className="invalid-feedback">{errors.upgradeAmount}</div>}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button type="button" className="btn btn-light" onClick={() => setShowUpgradeModal(false)}>Close</button>
                        <button type="submit" className="btn btn-primary" disabled={submitting}>
                            {submitting ? "Processing..." : "Confirm Upgrade"}
                        </button>
                    </Modal.Footer>
                </form>
            </Modal>

            <DeleteAlert
                show={showDeleteModal}
                handleClose={handleCloseDeleteModal}
                handleDelete={handleDelete}
            />
        </>
    );
};

export default ClientSubscriptions;
